import type { eventWithTime } from '@rrweb/types';
import { RECORDING_GAP_TAG } from '@daevio/shared/events';

const EV = { fullSnapshot: 2, incremental: 3, custom: 5 } as const;
const NODE = { element: 2 } as const;
const SOURCE = { mutation: 0, mouseMove: 1, mouseInteraction: 2, viewportResize: 4, touchMove: 6 } as const;

/** Incremental events that only move the pointer or resize the frame: safe to apply on any page. */
const POINTER_ONLY = new Set<number>([SOURCE.mouseMove, SOURCE.mouseInteraction, SOURCE.viewportResize, SOURCE.touchMove]);

interface SerializedNode {
  id?: number;
  type?: number;
  tagName?: string;
  childNodes?: SerializedNode[];
  [key: string]: unknown;
}

interface MutationData {
  source: number;
  adds?: { parentId: number; nextId?: number | null; node: SerializedNode }[];
  texts?: { id: number; value: string | null }[];
  attributes?: { id: number; attributes: Record<string, unknown> }[];
  removes?: { parentId: number; id: number }[];
  [key: string]: unknown;
}

function collectIds(node: SerializedNode, into: Set<number>): void {
  if (typeof node.id === 'number') into.add(node.id);
  for (const child of node.childNodes ?? []) collectIds(child, into);
}

/**
 * Prepares a recording for the player, one event at a time (the live view feeds it as chunks arrive).
 *
 * - **What is inside `<noscript>`.** With JavaScript on, a noscript holds its markup as plain text
 *   (`<iframe src=…googletagmanager…>`, `<style>…</style>`). rrweb hides `noscript` with a stylesheet in the replay
 *   document, but that does not reach shadow roots, and a noscript shown for any other reason printed that markup
 *   as text. The recorder no longer records it; this strips it from older recordings.
 * - **Changes after a lost batch.** Every page load numbers its nodes from 1. If a batch is lost (typically the full
 *   snapshot of a page left within a second), the changes of that page would be applied to the page before it, node
 *   ids pointing at unrelated nodes: text and CSS ended up in the wrong place. After a gap the DOM stays as it was
 *   until the next full snapshot; the pointer keeps moving.
 */
export class RecordingSanitizer {
  /** Ids of `<noscript>` elements, and of anything that was inside one, for the current full snapshot. */
  private noscript = new Set<number>();
  private hidden = new Set<number>();
  /** A batch is missing since the last full snapshot: DOM changes would land on the wrong nodes. */
  private stale = false;

  push(event: eventWithTime): eventWithTime | null {
    switch (event.type) {
      case EV.fullSnapshot: {
        this.noscript.clear();
        this.hidden.clear();
        this.stale = false;
        const data = event.data as { node: SerializedNode };
        const node = this.stripNode(data.node);
        return node === data.node ? event : ({ ...event, data: { ...data, node } } as eventWithTime);
      }
      case EV.custom: {
        const tag = (event.data as { tag?: unknown }).tag;
        if (tag === RECORDING_GAP_TAG) {
          this.stale = true;
          return null;
        }
        return event;
      }
      case EV.incremental: {
        const data = event.data as MutationData;
        if (this.stale) return POINTER_ONLY.has(data.source) ? event : null;
        if (data.source !== SOURCE.mutation) return event;
        const mutation = this.stripMutation(data);
        return mutation === data ? event : ({ ...event, data: mutation } as eventWithTime);
      }
      default:
        return event;
    }
  }

  /** The node with every noscript emptied; the same object when nothing changed. */
  private stripNode(node: SerializedNode): SerializedNode {
    if (node.type === NODE.element && node.tagName === 'noscript') {
      if (typeof node.id === 'number') this.noscript.add(node.id);
      if (!node.childNodes?.length) return node;
      for (const child of node.childNodes) collectIds(child, this.hidden);
      return { ...node, childNodes: [] };
    }
    const children = node.childNodes;
    if (!children?.length) return node;
    let changed = false;
    const next = children.map((child) => {
      const stripped = this.stripNode(child);
      if (stripped !== child) changed = true;
      return stripped;
    });
    return changed ? { ...node, childNodes: next } : node;
  }

  private stripMutation(data: MutationData): MutationData {
    let changed = false;
    let adds = data.adds;
    if (adds?.length) {
      const kept: NonNullable<MutationData['adds']> = [];
      for (const add of adds) {
        if (this.noscript.has(add.parentId) || this.hidden.has(add.parentId)) {
          collectIds(add.node, this.hidden);
          changed = true;
          continue;
        }
        const node = this.stripNode(add.node);
        if (node !== add.node) changed = true;
        kept.push(node === add.node ? add : { ...add, node });
      }
      adds = kept;
    }
    let texts = data.texts;
    if (texts?.length && this.hidden.size > 0) {
      const kept = texts.filter((t) => !this.hidden.has(t.id));
      if (kept.length !== texts.length) {
        texts = kept;
        changed = true;
      }
    }
    return changed ? { ...data, adds, texts } : data;
  }
}

/** The recording sorted by time, ready for the player (see {@link RecordingSanitizer}). */
export function sanitizeRecording(events: readonly eventWithTime[]): eventWithTime[] {
  const sanitizer = new RecordingSanitizer();
  const out: eventWithTime[] = [];
  for (const event of [...events].sort((a, b) => a.timestamp - b.timestamp)) {
    const kept = sanitizer.push(event);
    if (kept) out.push(kept);
  }
  return out;
}
